import { gql } from "@apollo/client";
import { COLOR_GREY_BORDER, COLOR_WHITE } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { gaEvent } from "helpers/gtag";
import useAction from "helpers/useAction";
import useShowMessage from "helpers/useShowMessage";
import Dialog, { DialogActionButton } from "molecules/Dialog";
import {
  CourtMarkings,
  ShotMarker,
  SVG_H,
  SVG_W,
  svgToGlX,
  svgToGlY,
  toSvgX,
  toSvgY,
} from "pages/games/BasketballCourt";
import React, { useLayoutEffect, useRef, useState } from "react";

// Lets a user place or correct the on-court location of a shot event.
// Saves x/y (0–100) directly via onsenUpsertEvent, independent of the highlight form.
export default function ShotPlacementDialog({ open, gameId, event, onClose, onSaved }) {
  const svgRef = useRef(null);
  const [pending, setPending] = useState(null);
  const showMessage = useShowMessage();

  const onsenUpsertEvent = useAction(gql`
    mutation ShotPlacementDialog($input: OnsenUpsertEventInput!) {
      onsenUpsertEvent(input: $input) {
        event {
          id
          coordinates {
            x
            y
          }
        }
      }
    }
  `);

  // seed from the event's existing coordinates each time the dialog opens
  useLayoutEffect(() => {
    if (open) setPending(event?.coordinates ? { x: event.coordinates.x, y: event.coordinates.y } : null);
  }, [open, event?.id]);

  const placeShot = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((clientX - rect.left) / rect.width) * SVG_W;
    const py = ((clientY - rect.top) / rect.height) * SVG_H;
    setPending({ x: svgToGlX(py), y: svgToGlY(px) });
  };

  const handleSave = async () => {
    // For an existing event, persist immediately. For a not-yet-created event
    // (the "Add Highlight" flow) just hand the coordinates back so they're
    // included when the highlight itself is saved.
    if (event.id) {
      await onsenUpsertEvent({
        input: { gameId, eventId: event.id, x: pending.x, y: pending.y },
      });
      showMessage("Shot location saved");
    }
    gaEvent("shot_location_save");
    onSaved?.(pending);
    onClose();
  };

  // NB: a plain Dialog (not FormDialog) is used on purpose. This dialog is rendered
  // inside the Add/Edit Highlight form, and React propagates events across portals,
  // so a nested <form> submit would bubble up and trigger the outer form's submit.
  return (
    <Dialog
      open={open}
      title="Shot Location"
      onClose={onClose}
      inverse
      actions={
        <>
          <DialogActionButton primary disabled={!pending} label="Save" onClick={handleSave} />
          <DialogActionButton label="Cancel" onClick={onClose} />
        </>
      }
    >
      <div style={{ fontFamily: FONT_FAMILY_GRAPHIK, fontSize: 14, color: COLOR_WHITE, opacity: 0.8 }}>
        Tap the court where the shot was taken. The hoop is at the top.
      </div>
      <div style={{ maxWidth: 360, width: "100%", margin: "0 auto" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            border: `1px solid ${COLOR_GREY_BORDER}`,
            cursor: "crosshair",
            touchAction: "none",
          }}
          onClick={(e) => placeShot(e.clientX, e.clientY)}
        >
          <CourtMarkings />
          {pending && <ShotMarker cx={toSvgX(pending.y)} cy={toSvgY(pending.x)} made={event?.made} />}
        </svg>
      </div>
    </Dialog>
  );
}
